import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ExternalLink } from '@factorialco/f0-react/icons/app'
import { OneWaitlistDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  isAiChatActive: boolean
  aiEnabled: boolean
  employeeName: string
  joinWaitlist?: (() => Promise<unknown>) | null
}

const useAiPromotion = ({ isAiChatActive, aiEnabled, employeeName, joinWaitlist }: Props) => {
  const { company, canRequestUpsell } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { data: oneWaitlistData, loading: oneWaitlistLoading } = useQuery(OneWaitlistDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const [isWaitlistLoading, setIsWaitlistLoading] = useState(false)

  const handleWaitlistJoin = async () => {
    if (!joinWaitlist || isWaitlistLoading) return

    try {
      track(trackingEvents.upsell.clickOneWaitlistButton, {
        source: 'one-chat',
      })

      setIsWaitlistLoading(true)

      const result = await joinWaitlist()

      if (result) {
        track(trackingEvents.upsell.joinedOneWaitlist, {
          source: 'one-chat',
        })
        app.setFlash({
          type: 'correct',
          message: i18n.t('growth.one_waitlist.animation.join_waitlist.success'),
        })
        window.location.reload()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('growth.one_waitlist.animation.join_waitlist.error'),
        })
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('growth.one_waitlist.animation.join_waitlist.error'),
      })
    } finally {
      setIsWaitlistLoading(false)
    }
  }

  const handleLearnMore = () => {
    track(trackingEvents.upsell.clickOneLandingLink, {
      source: 'one-chat',
    })
    navigate(navigation.discover.oneLandingPage.fullPath())
  }

  const handleSkipWaitlist = () => {
    track(trackingEvents.upsell.clickSkipWaitlistButton, {
      source: 'one-chat',
    })
    navigate(navigation.discover.onePromoPage.fullPath())
  }

  const copySets = {
    joinTheWaitlist: {
      title: i18n.t('growth.sidepanel_chat.waitlist.subtitle'),
      description: `${i18n.t('growth.sidepanel_chat.waitlist.description')} ${i18n.t(
        'growth.sidepanel_chat.waitlist.to'
      )}:`,
      benefits: [
        {
          noBoldText: i18n.t('growth.sidepanel_chat.bullet_points.get_access'),
          boldText: i18n.t('growth.sidepanel_chat.bullet_points.not_additional_cost'),
        },
        {
          noBoldText: i18n.t('growth.sidepanel_chat.bullet_points.key_features'),
          boldText: i18n.t('growth.sidepanel_chat.bullet_points.early'),
        },
        {
          noBoldText: i18n.t('growth.sidepanel_chat.bullet_points.share_feedback'),
          boldText: i18n.t('growth.sidepanel_chat.bullet_points.shape_one'),
        },
      ],
      actions: [
        {
          label: i18n.t('growth.sidepanel_chat.waitlist.join'),
          onClick: handleWaitlistJoin,
          buttonType: 'gradient',
          isLoading: isWaitlistLoading,
        },
        {
          label: i18n.t('growth.sidepanel_chat.learn_more'),
          onClick: handleLearnMore,
          buttonVariant: 'ghost',
          buttonType: 'internal',
          icon: ExternalLink,
          isLoading: false,
        },
      ],
    },
    alreadyOnWaitlist: {
      title: i18n.t('growth.sidepanel_chat.already_on_waitlist.subtitle'),
      description: i18n.t('growth.sidepanel_chat.already_on_waitlist.description'),
      actions: [
        {
          label: i18n.t('growth.sidepanel_chat.skip_waitlist'),
          onClick: handleSkipWaitlist,
          buttonType: 'gradient',
          isLoading: false,
        },
      ],
    },
    hasOneInactive: {
      title: i18n.t('growth.sidepanel_chat.ai_disabled.subtitle'),
      description: i18n.t('growth.sidepanel_chat.ai_disabled.description'),
    },
  }

  const isAiAgentFeatureEnabled = !!oneWaitlistData?.features.featuresConnection.nodes[0]?.enabled
  // If client doesn't have One and is already on the waitlist
  const alreadyInOneWaitlist =
    oneWaitlistData?.companies.featureAvailabilitiesConnection.nodes[0]?.available === true &&
    !aiEnabled
  // If client doesn't have One and is not on the waitlist yet
  const canJoinOneWaitlist =
    !!oneWaitlistData?.upsell._policies.canJoinOneWaitlist.can &&
    !aiEnabled &&
    !alreadyInOneWaitlist

  const handleOpen = () => {
    track(trackingEvents.upsell.viewOneSidepanel, {
      isOneActive: isAiChatActive,
      hasOnePolicy: aiEnabled,
      isAiAgentFeatureEnabled: isAiAgentFeatureEnabled,
      alreadyInOneWaitlist: alreadyInOneWaitlist,
      canJoinOneWaitlist: canJoinOneWaitlist,
    })
  }

  const handleClose = () => {
    track(trackingEvents.upsell.closeOneSidepanel, {
      isOneActive: isAiChatActive,
      hasOnePolicy: aiEnabled,
      isAiAgentFeatureEnabled: isAiAgentFeatureEnabled,
      alreadyInOneWaitlist: alreadyInOneWaitlist,
      canJoinOneWaitlist: canJoinOneWaitlist,
    })
  }

  let selectedCopy = null
  if (canJoinOneWaitlist) selectedCopy = copySets.joinTheWaitlist
  else if (alreadyInOneWaitlist) selectedCopy = copySets.alreadyOnWaitlist
  else if (aiEnabled && !isAiAgentFeatureEnabled) selectedCopy = copySets.hasOneInactive

  const aiPromotionEnabled = !isAiChatActive && selectedCopy !== null && canRequestUpsell

  const aiPromotion = {
    enabled: oneWaitlistLoading ? false : aiPromotionEnabled,
    greeting: i18n.t('common.ai.greeting', { name: employeeName }),
    onShow: handleOpen,
    onHide: handleClose,
    ...selectedCopy,
  }

  return { aiPromotion, canJoinOneWaitlist }
}

export default useAiPromotion
